/**
 * Universal Link / App Link handling for the Capacitor shell.
 *
 * When the OS opens the app via an https://app.turnkey.coach/… link (a push
 * tap, a shared link, an email link), Capacitor fires `appUrlOpen` with that
 * URL. Because the shell is a remote web view already sitting on
 * app.turnkey.coach, we navigate the in-app Inertia router to the link's path
 * rather than triggering a fresh full load — so the deep link lands on the right
 * screen without a white reload.
 *
 * This is the in-app half of deep linking; it only fires once the OS is
 * configured to route those links to the app (iOS Associated Domains entitlement
 * + apple-app-site-association; Android intent-filter + assetlinks.json). Until
 * then it simply never receives events. No-op off the Capacitor shell; never
 * throws.
 *
 * @capacitor/app is loaded lazily (dynamic import) so it is excluded from the
 * main entry bundle and never downloaded/parsed in MobiLoud or plain web —
 * only fetched when isCapacitorEnvironment() is true.
 */

import { router } from "@inertiajs/react";
import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

// Only follow links to our own hosts into the app; anything else is ignored
// (it would already have opened in the system browser).
const ALLOWED_HOSTS = ["app.turnkey.coach", "demo.turnkey.coach"];

function reportDeepLinkError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_deep_links" },
    });
  } catch {
    // reporting must never break the app
  }
}

/**
 * Extract the in-app path (pathname + query + hash) from a deep-link URL, or
 * null if it isn't one of our hosts / isn't parseable.
 */
export function pathFromDeepLink(url, allowedHosts = ALLOWED_HOSTS) {
  try {
    const parsed = new URL(url);
    if (!allowedHosts.includes(parsed.hostname)) {
      return null;
    }
    // Reject protocol-relative ("//evil.com") or backslash ("/\\evil.com")
    // paths: even though the host check passed, router.visit / location.assign
    // treat those as off-origin and would navigate the web view away to a
    // phishing site. A safe same-origin path starts with a single "/".
    if (/^\/[/\\]/.test(parsed.pathname)) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function navigateTo(path) {
  try {
    router.visit(path);
  } catch (error) {
    // If Inertia isn't ready for some reason, fall back to a plain navigation
    // (still within the web view, still on our origin).
    reportDeepLinkError(error);
    try {
      window.location.assign(path);
    } catch {
      // give up silently rather than throw out of an event handler
    }
  }
}

let registered = false;

/**
 * Route incoming Universal/App Links to the in-app router. No-op off-shell;
 * runs once; never throws.
 */
export function registerDeepLinks() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true;
  import("@capacitor/app")
    .then(({ App }) => {
      try {
        const result = App.addListener("appUrlOpen", ({ url }) => {
          const path = pathFromDeepLink(url);
          if (path) {
            navigateTo(path);
          }
        });
        if (result && typeof result.catch === "function") {
          result.catch(reportDeepLinkError);
        }
      } catch (error) {
        registered = false; // allow a later call to retry
        reportDeepLinkError(error);
      }
    })
    .catch((error) => {
      registered = false; // allow a later call to retry
      reportDeepLinkError(error);
    });
}
